import { useEffect, useCallback, useRef } from 'react'

interface UseOnScrollActiveTabOptions {
  offset?: number
  throttleMs?: number
}

export const useOnScrollActiveTab = (
  setTabIndex: (index: number) => void,
  options: UseOnScrollActiveTabOptions = {},
): void => {
  const { offset = 600, throttleMs = 16 } = options // 16ms ≈ 60fps
  const ticking = useRef(false)

  const handleScroll = useCallback(() => {
    // Get all tabs that have an ID defined
    const tabs = document.querySelectorAll<HTMLElement>('li[id]')

    // Get current scroll position
    const scrollY = window.pageYOffset

    // Now we loop through tabs to get height, top and ID values for each
    tabs.forEach((currentTab, index) => {
      const sectionHeight = currentTab.offsetHeight
      const sectionTop = currentTab.offsetTop + offset

      /*
        - If our current scroll position enters the space where current section on screen is, 
          add .active class to corresponding navigation link, else remove it
        - To know which link needs an active class, we use sectionId variable we are getting 
          while looping through sections as an selector
      */

      if (scrollY > sectionTop && scrollY <= sectionTop + sectionHeight) {
        setTabIndex(index)
      }
    })
  }, [setTabIndex, offset])

  const throttledHandleScroll = useCallback(() => {
    if (!ticking.current) {
      requestAnimationFrame(() => {
        handleScroll()
        ticking.current = false
      })
      ticking.current = true
    }
  }, [handleScroll])

  useEffect(() => {
    // Use passive listeners for better performance
    window.addEventListener('scroll', throttledHandleScroll, { passive: true })

    // Call once on mount to set initial state
    handleScroll()

    // Remove event listener for scroll
    return () => {
      window.removeEventListener('scroll', throttledHandleScroll)
      // Cancel any pending animation frame
      ticking.current = false
    }
  }, [throttledHandleScroll, handleScroll])
}
